import React from "react";
import Step1 from "src/assets/setupGuideImage/Step1.png";
import Step2 from "src/assets/setupGuideImage/Step2.png";
import Step3 from "src/assets/setupGuideImage/Step3.png";
import Step4 from "src/assets/setupGuideImage/Step4.png";
import Step5 from "src/assets/setupGuideImage/Step5.png";
import Step6 from "src/assets/setupGuideImage/Step6.png";
import Step7 from "src/assets/setupGuideImage/Step7.png";

const SetupGuideContent = () => {
  return (
    <div className="space-y-12 bg-white p-6 font-sans text-gray-800 md:p-10">
      <section className="space-y-4">
        <h2 className="text-2xl font-bold text-gray-900">Step 1: Create Your Account</h2>
        <p>Start your ClearSquare journey by creating your account. Enter your work email address and create a secure password to get started.</p>

        <div className="space-y-2">
          <h3 className="text-lg font-semibold">Work Email</h3>
          <p>Provide your business email address. This email will be used for account communication, notifications, and future access to your ClearSquare portal.</p>
        </div>

        <div className="space-y-2">
          <h3 className="text-lg font-semibold">Create Password</h3>
          <p>Create a strong password that meets the required security criteria:</p>
          <ul className="list-disc space-y-1 pl-5 text-gray-600">
            <li>Minimum 8 characters</li>
            <li>At least two lowercase letters</li>
            <li>At least one uppercase letter</li>
            <li>At least one number</li>
            <li>At least one special character (such as @, #, $, %, etc.)</li>
          </ul>
        </div>

        <p className="font-medium">Once your details are entered and all password requirements are satisfied, click Next to continue to the next step.</p>

        <div className="pt-2">
          <img
            src={Step1}
            alt="Step 1"
            className="w-full rounded-lg border border-gray-200 shadow-sm"
          />
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="text-2xl font-bold text-gray-900">Step 2: Add Your Account Information</h2>
        <p>Tell us a bit about yourself and your company. This information helps us personalize your ClearSquare experience and set up your account correctly.</p>

        <div className="space-y-2">
          <h3 className="text-lg font-semibold">Profile Photo</h3>
          <p>Upload your profile photo to help personalize your account. You can add or update your photo by clicking Change.</p>
        </div>

        <div className="space-y-2">
          <h3 className="text-lg font-semibold">Your Name</h3>
          <p>Enter your full name. This will be used to identify you within your ClearSquare portal.</p>
        </div>

        <div className="space-y-2">
          <h3 className="text-lg font-semibold">Company Logo</h3>
          <p>Upload your company logo to customize your account and workspace experience. You can update your logo anytime by clicking Change.</p>
        </div>

        <div className="space-y-2">
          <h3 className="text-lg font-semibold">Company Name</h3>
          <p>Enter your company's official name. This information will be displayed across your ClearSquare account.</p>
        </div>

        <div className="space-y-2">
          <h3 className="text-lg font-semibold">Terms & Privacy Agreement</h3>
          <p>Review and accept ClearSquare's Terms of Service and Privacy Policy to continue.</p>
        </div>

        <p className="font-medium">Once all required information is completed and the agreement checkbox is selected, click Next to proceed to the plan selection step.</p>

        <div className="pt-2">
          <img
            src={Step2}
            alt="Step 2"
            className="w-full rounded-lg border border-gray-200 shadow-sm"
          />
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="text-2xl font-bold text-gray-900">Step 3: Choose Your Plan</h2>
        <p>Select the plan that best fits your business needs. Each plan includes different features, workspace limits, user access, and customization options.</p>

        <div className="space-y-2">
          <h3 className="text-lg font-semibold">Review Available Plans</h3>
          <p>Explore the available subscription plans and compare features such as:</p>
          <ul className="list-disc space-y-1 pl-5 text-gray-600">
            <li>Number of workspaces and users</li>
            <li>Custom branding options</li>
            <li>API access and integrations</li>
            <li>Security features</li>
            <li>Support options</li>
            <li>Advanced tools and business capabilities</li>
          </ul>
        </div>

        <div className="space-y-2">
          <h3 className="text-lg font-semibold">Select Billing Frequency</h3>
          <p>Choose your preferred billing cycle:</p>
          <ul className="list-disc space-y-1 pl-5 text-gray-600">
            <li>Monthly Billing – Pay month-to-month</li>
            <li>Yearly Billing – Pay annually (if available)</li>
          </ul>
        </div>

        <div className="space-y-2">
          <h3 className="text-lg font-semibold">Confirm Your Selection</h3>
          <p>After selecting your preferred plan and billing option, review the pricing summary, including your trial details and total amount due.</p>
        </div>

        <p className="font-medium">Click Next to continue to the payment details step.</p>

        <div className="pt-2">
          <img
            src={Step3}
            alt="Step 3"
            className="w-full rounded-lg border border-gray-200 shadow-sm"
          />
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="text-2xl font-bold text-gray-900">Step 4: Add Payment Details</h2>
        <p>Complete your setup by providing your payment information. Your selected plan details and billing information will be displayed before you submit your payment details.</p>

        <div className="space-y-2">
          <h3 className="text-lg font-semibold">Review Your Selected Plan</h3>
          <p>Confirm your chosen subscription plan, included features, pricing, and billing cycle.</p>
        </div>

        <div className="space-y-2">
          <h3 className="text-lg font-semibold">Free Trial</h3>
          <p>Your account includes a 60-day free trial. After the trial period ends, your subscription will continue automatically based on your selected plan unless canceled.</p>
        </div>

        <div className="space-y-2">
          <h3 className="text-lg font-semibold">Enter Card Information</h3>
          <p>Provide your payment details:</p>
          <ul className="list-disc space-y-1 pl-5 text-gray-600">
            <li>Card Number – Enter your valid credit or debit card number.</li>
            <li>Expiration Date – Enter your card expiry date.</li>
            <li>Security Code – Enter your card's security code (CVV).</li>
            <li>Country – Select your billing country.</li>
            <li>ZIP Code – Enter your billing postal code.</li>
          </ul>
        </div>

        <div className="space-y-2">
          <h3 className="text-lg font-semibold">Confirm Payment Authorization</h3>
          <p>By providing your card information, you authorize ClearSquare to charge your card for future payments according to the subscription terms.</p>
        </div>

        <p className="font-medium">Once all payment details are completed, click Next to finish the registration process.</p>

        <div className="pt-2">
          <img
            src={Step4}
            alt="Step 4"
            className="w-full rounded-lg border border-gray-200 shadow-sm"
          />
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="text-2xl font-bold text-gray-900">Registration Complete</h2>
        <p>Your registration is complete! Your ClearSquare account setup has been successfully submitted, and we are now preparing your instance.</p>

        <div className="space-y-2">
          <h3 className="text-lg font-semibold">Instance Setup in Progress</h3>
          <p>Our system is currently deploying and configuring your ClearSquare environment. This process may take some time while we complete the required setup.</p>
        </div>

        <div className="space-y-2">
          <h3 className="text-lg font-semibold">What Happens Next?</h3>
          <ul className="list-disc space-y-1 pl-5 text-gray-600">
            <li>You can safely close this page if needed.</li>
            <li>We will notify you via your registered email address once your instance is ready.</li>
            <li>Once setup is complete, you can access your ClearSquare portal and start exploring your analytics workspace.</li>
          </ul>
        </div>

        <p className="font-medium">Thank you for choosing ClearSquare. We’re preparing everything you need to get started.</p>

        <div className="pt-2">
          <img
            src={Step5}
            alt="Registration Complete"
            className="w-full rounded-lg border border-gray-200 shadow-sm"
          />
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="text-2xl font-bold text-gray-900">Your Instance Is Ready</h2>
        <p>Congratulations! Your ClearSquare setup is complete, and your instance is ready to use.</p>

        <div className="space-y-2">
          <h3 className="text-lg font-semibold">Access Your Instance</h3>
          <p>Your dedicated ClearSquare environment has been successfully created. Click the Link button to access your instance and start exploring your analytics workspace.</p>
        </div>

        <div className="space-y-2">
          <h3 className="text-lg font-semibold">Next Steps</h3>
          <ul className="list-disc space-y-1 pl-5 text-gray-600">
            <li>Open your instance using the provided link.</li>
            <li>Sign in with your account credentials.</li>
            <li>Start configuring and using your ClearSquare portal.</li>
          </ul>
        </div>

        <p className="font-medium">Welcome to ClearSquare — your analytics workspace is ready!</p>

        <div className="pt-2">
          <img
            src={Step6}
            alt="Your Instance Is Ready"
            className="w-full rounded-lg border border-gray-200 shadow-sm"
          />
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="text-2xl font-bold text-gray-900">Access Your New Portal</h2>
        <p>After your instance setup is complete, click the provided Link button to open your newly created ClearSquare portal.</p>
        <p>You will be redirected to a brand-new portal login page where you can access your account.</p>

        <div className="space-y-2">
          <h3 className="text-lg font-semibold">Login Using Your Credentials</h3>
          <p>Enter the credentials you used during registration:</p>
          <ul className="list-disc space-y-1 pl-5 text-gray-600">
            <li>Email Address – Use your registered email address.</li>
            <li>Password – Enter the password created during the signup process.</li>
          </ul>
        </div>

        <p className="font-medium">Click Login to access your portal and start using ClearSquare.</p>

        <div className="space-y-2">
          <h3 className="text-lg font-semibold">Additional Login Options</h3>
          <p>If your organization has enabled Single Sign-On (SSO), you can also use the SSO Login option to sign in securely.</p>
        </div>

        <div className="pt-2">
          <img
            src={Step7}
            alt="Access Your New Portal"
            className="w-full rounded-lg border border-gray-200 shadow-sm"
          />
        </div>
      </section>
    </div>
  );
};

export default SetupGuideContent;
